import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useStoreContext } from '../context/StoreContext';

const DEFAULT_THEME = {
    primary_color: "#1a365d",
    secondary_color: "#2c5282",
    accent_color: "#d69e2e",
    background_color: "#ffffff",
    header_bg_color: "#ffffff",
    text_color: "#1a202c",
    font_family: "Cormorant Garamond, serif"
};

export function useStore(subdomain) {
    // Try to get context first
    let contextData = null;
    try {
        contextData = useStoreContext();
    } catch (e) {
        // Not in provider, fall back to logic below
    }

    const [storeState, setStoreState] = useState(null);
    const [themeState, setThemeState] = useState(DEFAULT_THEME);
    const [storeAppearanceState, setStoreAppearanceState] = useState({});
    const [platformInfoState, setPlatformInfoState] = useState({});
    const [shippingRatesState, setShippingRatesState] = useState({
        shipping_enabled: true,
        free_shipping_threshold: 500,
        shipping_tiers: []
    });
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [collections, setCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // If context is available, return its data but allow products/categories to be fetched if needed
    // (though context has collections too)
    
    useEffect(() => {
        // Only skip fetching if context actually has store data loaded.
        // An empty/default context object is truthy but has no store — don't bail out early.
        if (contextData && contextData.store) {
            setLoading(false);
            return;
        }

        const fetchStoreData = async () => {
    try {
        setLoading(true);
        
        // 1. Sanitize subdomain first
        let cleanSubdomain = "";
        if (subdomain) {
            cleanSubdomain = decodeURIComponent(subdomain);
            if (cleanSubdomain.includes('/store/')) {
                cleanSubdomain = cleanSubdomain.split('/store/')[1].split('/')[0].split('?')[0];
            } else if (cleanSubdomain.startsWith('http')) {
                cleanSubdomain = cleanSubdomain.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0].split('.')[0];
            }
        }

        // 2. Fetch everything in parallel
        const promises = [
            axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/theme-presets`),
            axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/store-appearance`),
            axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/platform-info`),
            axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/shipping-rates`)
        ];

        if (cleanSubdomain) {
            promises.push(axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/preview/${cleanSubdomain}`));
        }

        const responses = await Promise.all(promises);
        
        const themePresets = responses[0].data;
        const appData = responses[1].data;
        const platInfo = responses[2].data;
        const shipRates = responses[3].data;
        const previewData = cleanSubdomain ? responses[4].data : null;

        setStoreAppearanceState(appData);
        setPlatformInfoState(platInfo);
        setShippingRatesState(shipRates);

        let storeData;
        if (previewData) {
            storeData = previewData.store;
            setProducts(previewData.products || []);
            setCategories(previewData.categories || []);
            setCollections(previewData.collections || []);
        } else {
            storeData = { ...platInfo, theme: {} };
        }

        setStoreState(storeData);

        // Resolve Theme
        const presetName = storeData?.theme?.preset || "classic_elegant";
        const activePreset = themePresets[presetName] || {};

        setThemeState({
            primary_color: storeData?.theme?.custom_primary_color || activePreset.primary_color || appData.primary_color || DEFAULT_THEME.primary_color,
            secondary_color: storeData?.theme?.custom_secondary_color || activePreset.secondary_color || DEFAULT_THEME.secondary_color,
            accent_color: storeData?.theme?.custom_accent_color || activePreset.accent_color || DEFAULT_THEME.accent_color,
            background_color: storeData?.theme?.custom_background_color || activePreset.background_color || DEFAULT_THEME.background_color,
            header_bg_color: storeData?.theme?.custom_header_bg_color || activePreset.header_bg_color || activePreset.primary_color || DEFAULT_THEME.header_bg_color,
            text_color: storeData?.theme?.custom_text_color || activePreset.text_color || DEFAULT_THEME.text_color,
            font_family: storeData?.theme?.custom_font_family || activePreset.font_family || appData.font || DEFAULT_THEME.font_family
        });

    } catch (err) {
        console.error("Failed to fetch store data:", err);
        setError(err);
    } finally {
        setLoading(false);
    }
};

        fetchStoreData();
    }, [subdomain, contextData]);

    if (contextData && contextData.store) {
        return {
            store: contextData.store,
            theme: contextData.theme,
            storeAppearance: contextData.storeAppearance,
            platformInfo: contextData.platformInfo,
            shippingRates: contextData.shippingRates,
            products: contextData.products,
            categories: contextData.categories,
            collections: contextData.collections,
            loading: contextData.loading,
            error: contextData.error
        };
    }

    return {
        store: storeState,
        theme: themeState,
        storeAppearance: storeAppearanceState,
        platformInfo: platformInfoState,
        shippingRates: shippingRatesState,
        products,
        categories,
        collections,
        loading,
        error
    };
}
